'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole = 'parent' | 'player' | 'admin';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  avatarUrl?: string;
  isActive: boolean;
  subscriptionPlan?: string;
  createdAt: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  role: UserRole | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  isParent: () => boolean;
  isPlayer: () => boolean;
  isAdmin: () => boolean;
  canAccess: (route: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Route access rules per role
const ROLE_ACCESS: Record<UserRole, string[]> = {
  parent: [
    '/platform-dashboard',
    '/parent-dashboard',
    '/message-boards',
    '/games',
    '/payment-checkout',
    '/billing-history',
    '/parent-settings',
    '/add-child',
    '/verification',
    '/account',
  ],
  player: [
    '/platform-dashboard',
    '/message-boards',
    '/games',
    '/verification',
    '/account',
  ],
  admin: [
    '/platform-dashboard',
    '/admin-verification',
    '/admin-control-center',
    '/admin-moderation',
    '/parent-dashboard',
    '/message-boards',
    '/games',
    '/parent-settings',
    '/billing-history',
    '/account',
  ],
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error || !data) return null;
      return {
        id: data.id,
        email: data.email,
        fullName: data.full_name,
        firstName: data.first_name,
        lastName: data.last_name,
        role: data.role as UserRole,
        avatarUrl: data.avatar_url,
        isActive: data.is_active,
        subscriptionPlan: data.subscription_plan,
        createdAt: data.created_at,
      };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let isHandlingTokenError = false;

    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      // If the stored refresh token is invalid/expired, clear it and treat as signed out
      if (error && ((error as any).code === 'refresh_token_not_found' || (error as any).status === 400)) {
        if (!isHandlingTokenError) {
          isHandlingTokenError = true;
          await supabase.auth.signOut({ scope: 'local' });
        }
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    }).catch(() => {
      // Network-level failure (e.g. "Failed to fetch" during token refresh)
      // Clear any stale local session data and treat as signed out
      try { supabase.auth.signOut({ scope: 'local' }); } catch {}
      setSession(null);
      setUser(null);
      setProfile(null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Handle token refresh failures — clear local session without network call
      if (event === 'TOKEN_REFRESHED' && !session) {
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || `${metadata?.firstName || ''} ${metadata?.lastName || ''}`.trim(),
          first_name: metadata?.firstName || '',
          last_name: metadata?.lastName || '',
          role: metadata?.role || 'player',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const isParent = () => profile?.role === 'parent';
  const isPlayer = () => profile?.role === 'player';
  const isAdmin = () => profile?.role === 'admin';

  const canAccess = (route: string): boolean => {
    if (!profile?.role) return false;
    const allowed = ROLE_ACCESS[profile.role] || [];
    return allowed.some((r) => route.startsWith(r));
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    role: profile?.role ?? null,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    isParent,
    isPlayer,
    isAdmin,
    canAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
